import { SolutionPageContent } from '@/types/scenarios/solution';

/**
 * Default page content that can be used as a fallback when a solution
 * doesn't have custom pageContent defined. This provides generic but
 * relevant content based on the market category.
 */

// Generic chat scenarios that work across most industries
const genericChatScenarios = [
  {
    id: 'emergency',
    title: 'Emergency Calls',
    messages: [
      { type: 'user' as const, text: "I have an urgent issue that needs immediate attention!", delay: 300 },
      { type: 'genie' as const, text: "I understand this is urgent. Let me get your details so our team can call you back right away.", delay: 1200 },
      { type: 'user' as const, text: "Okay, please hurry", delay: 2500 },
      { type: 'genie' as const, text: "Of course. What's your name and the best number to reach you?", delay: 3400 },
      { type: 'user' as const, text: "John Smith, 555-234-5678", delay: 4600 },
      { type: 'genie' as const, text: "Got it, John. I've marked this as urgent - you'll receive a callback within 15 minutes.", delay: 5500 },
    ],
    outcomes: [
      { id: '1', text: 'Contact details captured', delay: 6500 },
      { id: '2', text: 'Issue severity assessed', delay: 6700 },
      { id: '3', text: 'Marked as urgent (P1)', delay: 6900 },
      { id: '4', text: 'Callback scheduled within 15 min', delay: 7100 },
      { id: '5', text: 'Customer reassured', delay: 7300 },
    ],
  },
  {
    id: 'product_inquiry',
    title: 'Product Inquiries',
    messages: [
      { type: 'user' as const, text: "I'm interested in learning more about your services", delay: 300 },
      { type: 'genie' as const, text: "I'd be happy to help! What specific information are you looking for?", delay: 1200 },
      { type: 'user' as const, text: "What are your pricing options?", delay: 2500 },
      { type: 'genie' as const, text: "We have several packages to fit different needs. Would you like someone to walk you through the options?", delay: 3400 },
      { type: 'user' as const, text: "Yes, that would be helpful", delay: 4600 },
      { type: 'genie' as const, text: "Perfect. What's your name and the best time to reach you?", delay: 5500 },
    ],
    outcomes: [
      { id: '1', text: 'Lead information captured', delay: 6500 },
      { id: '2', text: 'Interest level identified', delay: 6700 },
      { id: '3', text: 'Questions addressed', delay: 6900 },
      { id: '4', text: 'Follow-up call scheduled', delay: 7100 },
      { id: '5', text: 'Qualified lead created', delay: 7300 },
    ],
  },
  {
    id: 'support',
    title: 'Support Questions',
    messages: [
      { type: 'user' as const, text: "I need help with something", delay: 300 },
      { type: 'genie' as const, text: "I'm here to help! What do you need assistance with?", delay: 1200 },
      { type: 'user' as const, text: "I'm having trouble accessing my account", delay: 2500 },
      { type: 'genie' as const, text: "I can help with that. First, let me get some information to locate your account.", delay: 3400 },
      { type: 'user' as const, text: "Sure, what do you need?", delay: 4600 },
      { type: 'genie' as const, text: "What's the email address associated with your account?", delay: 5500 },
    ],
    outcomes: [
      { id: '1', text: 'Issue documented', delay: 6500 },
      { id: '2', text: 'Account identified', delay: 6700 },
      { id: '3', text: 'Basic troubleshooting attempted', delay: 6900 },
      { id: '4', text: 'Support ticket created', delay: 7100 },
      { id: '5', text: 'Follow-up scheduled', delay: 7300 },
    ],
  },
  {
    id: 'scheduling',
    title: 'Appointment Booking',
    messages: [
      { type: 'user' as const, text: "I'd like to schedule an appointment", delay: 300 },
      { type: 'genie' as const, text: "I can help you with that! What type of service do you need?", delay: 1200 },
      { type: 'user' as const, text: "I need a consultation", delay: 2500 },
      { type: 'genie' as const, text: "Great. Do you have a preferred day and time?", delay: 3400 },
      { type: 'user' as const, text: "Sometime next week in the morning would be ideal", delay: 4600 },
      { type: 'genie' as const, text: "I have availability Tuesday and Thursday morning. Which works better for you?", delay: 5500 },
    ],
    outcomes: [
      { id: '1', text: 'Service type identified', delay: 6500 },
      { id: '2', text: 'Availability checked', delay: 6700 },
      { id: '3', text: 'Appointment booked', delay: 6900 },
      { id: '4', text: 'Confirmation sent', delay: 7100 },
      { id: '5', text: 'Calendar updated', delay: 7300 },
    ],
  },
  {
    id: 'after_hours',
    title: 'After Hours',
    messages: [
      { type: 'user' as const, text: "Hi, I know it's late but I have a question", delay: 300 },
      { type: 'genie' as const, text: "No problem at all! I'm available 24/7 to help. What can I do for you?", delay: 1200 },
      { type: 'user' as const, text: "I wanted to check on the status of my order", delay: 2500 },
      { type: 'genie' as const, text: "I can look into that for you. What's your order number or the name on the account?", delay: 3400 },
      { type: 'user' as const, text: "It's under Mike Johnson", delay: 4600 },
      { type: 'genie' as const, text: "Thanks Mike. I'll have our team follow up with the details first thing tomorrow morning.", delay: 5500 },
    ],
    outcomes: [
      { id: '1', text: 'After-hours inquiry captured', delay: 6500 },
      { id: '2', text: 'Customer information collected', delay: 6700 },
      { id: '3', text: 'Request documented', delay: 6900 },
      { id: '4', text: 'Follow-up queued', delay: 7100 },
      { id: '5', text: 'Customer satisfaction maintained', delay: 7300 },
    ],
  },
];

// Generic use case cards - uses market-specific generated images
const getGenericUseCaseCards = (market: string) => [
  {
    id: 'lead_capture',
    title: '24/7 Lead Capture',
    description: "Never miss a potential customer. Your Genie captures and qualifies leads around the clock, even when you're closed.",
    bullets: [
      'Capture leads outside business hours',
      'Qualify prospects automatically',
      'Send instant notifications to your team',
      'Never lose another opportunity',
    ],
    image: `/solutions-pages/${market}/usecase-lead-capture.webp`,
  },
  {
    id: 'customer_support',
    title: 'Instant Support',
    description: "Answer common questions instantly and route complex issues to the right team member.",
    bullets: [
      'Answer FAQs automatically',
      'Route urgent issues appropriately',
      'Create support tickets',
      'Improve customer satisfaction',
    ],
    image: `/solutions-pages/${market}/usecase-test-drives.webp`,
  },
  {
    id: 'appointment_booking',
    title: 'Smart Scheduling',
    description: "Let customers book appointments anytime. Your Genie handles scheduling while you focus on your work.",
    bullets: [
      'Book appointments 24/7',
      'Sync with your calendar',
      'Send automatic reminders',
      'Reduce no-shows',
    ],
    image: `/solutions-pages/${market}/usecase-service.webp`,
  },
  {
    id: 'emergency_routing',
    title: 'Emergency Handling',
    description: "Urgent calls get the attention they deserve. Your Genie triages and routes emergencies instantly.",
    bullets: [
      'Identify urgent situations',
      'Route to on-call staff',
      'Escalate appropriately',
      'Keep customers informed',
    ],
    image: `/solutions-pages/${market}/usecase-financing.webp`,
  },
];

// Generic CTA pillars - uses market-specific background image
const getGenericFinalCTA = (market: string) => ({
  title: "Your Business Never Sleeps",
  subtitle: "While you rest, your Genie captures every lead, handles every question, and ensures no opportunity slips through the cracks.",
  pillars: [
    {
      title: "Always\nOn",
      gradient: "from-purple-400 to-blue-400",
      description: "Capture every call, even at 2am when competitors go to voicemail.",
    },
    {
      title: "Always\nReady",
      gradient: "from-blue-400 to-cyan-400",
      description: "Route emergencies instantly while you sleep soundly.",
    },
    {
      title: "Always\nYou",
      gradient: "from-cyan-400 to-teal-400",
      description: "Your voice, your brand — customers never know the difference.",
    },
  ],
  backgroundImage: `/solutions-pages/${market}/cta-background.webp`,
});

/**
 * Returns default page content based on the market category.
 * Uses market-specific generated images from /solutions-pages/{market}/.
 */
export const getDefaultPageContent = (market: string): SolutionPageContent => {
  return {
    scenariosHeader: {
      title: "Never miss another opportunity\neven when you're closed",
      subtitle: "See how your Genie handles real conversations — capturing leads, answering questions, and keeping customers happy.",
    },
    chatScenarios: genericChatScenarios,
    useCasesHeader: {
      title: "Stop Losing Business",
      subtitle: "Every missed call is a missed opportunity. See how businesses like yours are capturing revenue around the clock.",
    },
    useCaseCards: getGenericUseCaseCards(market),
    finalCTA: getGenericFinalCTA(market),
  };
};
